import React, { useState, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faCalendarAlt,
  faChurch,
  faFilter
} from '@fortawesome/free-solid-svg-icons';
import { fetchCatholicFestivities, fetchCatholicFestivitiesInRange } from '../services/catholicFestivityService';
import { useLanguage } from '../contexts/LanguageContext';
import './MobileEventsList.css';

function MobileEventsList({ events, onEventSelect, onCreateEvent }) {
  const { t } = useLanguage();
  const [activeFilter, setActiveFilter] = useState('upcoming'); // 'upcoming', 'past', 'all'
  const [festivities, setFestivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  
  // Get current date for filtering
  const today = useMemo(() => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
  }, []); // Empty dependency array means this only runs once
  today.setHours(0, 0, 0, 0);


  // Format month abbreviation
  const getMonthAbbr = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('sl-SI', { month: 'short' });
  };

  // Format day number
  const getDayNumber = (dateStr) => {
    const date = new Date(dateStr);
    return date.getDate();
  };

  // Format time
  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    
    // If the time already doesn't have seconds, return as is
    if (!timeStr.includes(':')) return timeStr;
    
    // Check if time has the format HH:MM:SS
    const timeParts = timeStr.split(':');
    if (timeParts.length === 3) {
      // Return only hours and minutes (HH:MM)
      return `${timeParts[0]}:${timeParts[1]}`;
    }
    
    // Return original time if it doesn't match expected format
    return timeStr;
  };

  // Filter events based on active filter
  const filteredEvents = () => {
    if (!events || events.length === 0) return [];
    
    switch (activeFilter) {
      case 'upcoming':
        return events.filter(event => {
          const eventDate = new Date(event.date);
          eventDate.setHours(0, 0, 0, 0);
          return eventDate >= today;
        }).sort((a, b) => new Date(a.date) - new Date(b.date));
      
      case 'past':
        return events.filter(event => {
          const eventDate = new Date(event.date);
          eventDate.setHours(0, 0, 0, 0);
          return eventDate < today;
        }).sort((a, b) => new Date(b.date) - new Date(a.date)); // Most recent first
      
      case 'all':
      default:
        return [...events].sort((a, b) => new Date(a.date) - new Date(b.date));
    }
  };

  // Load festivities
  useEffect(() => {
    const loadFestivities = async () => {
      setLoading(true);
      try {
        // Try to use the new range-based endpoint
        let festivitiesData;
        try {
          festivitiesData = await fetchCatholicFestivitiesInRange(today, 30);
        } catch (error) {
          
          // Fallback to monthly fetching if needed
          const currentYear = today.getFullYear();
          const currentMonth = today.getMonth() + 1;
          const currentMonthFestivities = await fetchCatholicFestivities(currentYear, currentMonth);
          
          // Calculate next month to check if we need festivities from the next month too
          const nextMonth = new Date(today);
          nextMonth.setDate(today.getDate() + 30);
          
          let allFestivities = [...currentMonthFestivities];
          
          // If our 30-day window extends into the next month, fetch those festivities too
          const nextMonthNumber = nextMonth.getMonth() + 1;
          const nextMonthYear = nextMonth.getFullYear();
          
          if (currentMonth !== nextMonthNumber || currentYear !== nextMonthYear) {
            const nextMonthFestivities = await fetchCatholicFestivities(nextMonthYear, nextMonthNumber);
            allFestivities = [...allFestivities, ...nextMonthFestivities];
          }
          
          // Filter to only show festivities in the next 30 days
          festivitiesData = allFestivities.filter(festivity => {
            const festivityDate = new Date(festivity.date);
            return festivityDate >= today && festivityDate <= nextMonth;
          });
        }
        
        // Sort by date
        const sortedFestivities = festivitiesData.sort(
          (a, b) => new Date(a.date) - new Date(b.date)
        );
        
        setFestivities(sortedFestivities);
      } catch (error) {
        setFestivities([]);
      } finally {
        setLoading(false);
      }
    };
    
    loadFestivities();
  }, [today]);

  // Toggle filters modal
  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  // Handle filter selection
  const handleFilterSelect = (filter) => {
    setActiveFilter(filter);
    setShowFilters(false);
  };

  // Helper function to get the right color class based on festivity type (now in Slovenian)
  const getFestivityColorClass = (festivity) => {
    // First check the color directly if available
    if (festivity.color) {
      const color = festivity.color.toLowerCase();
      switch(color) {
        case 'zelena':
        case 'green':
          return 'bg-success';
        case 'vijolična':
        case 'purple':
        case 'violet':
          return 'bg-purple';
        case 'rdeča':
        case 'red':
          return 'bg-danger';
        case 'bela':
        case 'white':
        case 'zlata':
        case 'gold':
          return 'bg-warning';
        case 'rožnata':
        case 'rose':
          return 'bg-pink';
          default:
            return 'bg-primary'; // Default color if none matches
      }
    }

    // If no color, check the festivity type - now in Slovenian
    if (festivity.festivity_type) {
      const type = festivity.festivity_type.toLowerCase();
      switch(type) {
        case 'slovesnost':
        case 'solemnity':
          return 'bg-warning';
        case 'praznik':
        case 'feast':
          return 'bg-info';
        case 'spomin':
        case 'memorial':
          return 'bg-secondary';
        case 'neobvezni_spomin':
        case 'optional_memorial':
          return 'bg-light text-dark';
        case 'liturgično_obdobje':
        case 'liturgical_season':
          return 'bg-primary';
        default:
          return 'bg-primary';
      }
    }
    
    // Default color
    return 'bg-primary';
  };

  return (
    <div className="mobile-events-list">
      {/* Header */}
      <div className="mobile-events-header">
        <div className="mobile-events-header-content">
          <h1>{t('events.title')}</h1>
          <div className="mobile-events-actions">
            <button
              className="mobile-header-button"
              onClick={toggleFilters}
              aria-label={t('events.filters.filterEvents')}
            >
              <FontAwesomeIcon icon={faFilter} />
            </button>
            <button
              className="mobile-header-button"
              onClick={onCreateEvent}
              aria-label={t('events.newEvent')}
            >
              <FontAwesomeIcon icon={faPlus} />
            </button>
          </div>
        </div>

        {/* Filter tabs */}
        <div className="mobile-events-tabs">
          <button
            className={`mobile-events-tab ${activeFilter === 'upcoming' ? 'active' : ''}`}
            onClick={() => handleFilterSelect('upcoming')}
          >
            {t('events.filters.upcoming')}
          </button>
          <button
            className={`mobile-events-tab ${activeFilter === 'past' ? 'active' : ''}`}
            onClick={() => handleFilterSelect('past')}
          >
            {t('events.filters.past')}
          </button>
          <button
            className={`mobile-events-tab ${activeFilter === 'all' ? 'active' : ''}`}
            onClick={() => handleFilterSelect('all')}
          >
            {t('events.filters.all')}
          </button>
        </div>
      </div>
      
      {/* Events list */}
      <div className="mobile-events-content">
        {loading ? (
          <div className="mobile-events-loading">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">{t('events.loading')}</span>
            </div>
          </div>
        ) : (
          <>
            {filteredEvents().length > 0 ? (
              <div className="mobile-events-items">
                {filteredEvents().map((event) => (
                  <div 
                    key={event.id} 
                    className="mobile-event-item"
                    onClick={() => onEventSelect(event.id)}
                  >
                    <div className="mobile-event-date-block">
                      <div className="month">{getMonthAbbr(event.date)}</div>
                      <div className="day">{getDayNumber(event.date)}</div>
                    </div>
                    <div className="mobile-event-details">
                      <div className="mobile-event-name">{event.title || t('events.noTitle')}</div>
                      <div className="mobile-event-subtitle">
                        {event.time ?
                          `${new Date(event.date).toLocaleDateString('sl-SI', { weekday: 'short' })} ${formatTime(event.time)}` :
                          new Date(event.date).toLocaleDateString('sl-SI', { weekday: 'long' })
                        }
                        {event.location && <span className="venue"> • {event.location}</span>}
                      </div>
                    </div>
                    <div className="mobile-event-action">
                      <FontAwesomeIcon icon={faPlus} />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="mobile-events-empty">
                <FontAwesomeIcon icon={faCalendarAlt} className="empty-icon" />
                <p>
                  {activeFilter === 'upcoming' ? t('events.empty.noUpcoming') :
                   activeFilter === 'past' ? t('events.empty.noPast') :
                   t('events.empty.noEvents')}
                </p>
                <button className="btn btn-primary" onClick={onCreateEvent}>
                  <FontAwesomeIcon icon={faPlus} className="me-2" />
                  {t('events.newEvent')}
                </button>
              </div>
            )}
            
            {/* Show festivities section if there are any and we're in upcoming view */}
            {activeFilter === 'upcoming' && festivities.length > 0 && (
              <div className="mobile-festivities-section">
                <h2 className="mobile-section-title">
                  <FontAwesomeIcon icon={faChurch} className="me-2" />
                  {t('events.calendar.festivities')}
                </h2>
                <div className="mobile-festivities-list">
                  {festivities.map((festivity) => (
                    <div key={festivity.id} className="mobile-festivity-item">
                      <div className={`mobile-event-date-block ${getFestivityColorClass(festivity)}`}>
                        <div className="month">{getMonthAbbr(festivity.date)}</div>
                        <div className="day">{getDayNumber(festivity.date)}</div>
                      </div>
                      <div className="mobile-event-details">
                        <div className="mobile-event-name">
                          {festivity.name}
                          {festivity.is_holy_day && (
                            <span className="badge bg-danger ms-2" style={{ fontSize: '0.65rem' }}>{t('events.calendar.mandatoryBadge')}</span>
                          )}
                        </div>
                        <div className="mobile-event-subtitle">
                          {new Date(festivity.date).toLocaleDateString('sl-SI', { weekday: 'long' })}
                          {festivity.festivity_type && (
                            <span className="venue"> • {festivity.festivity_type}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
      
      {/* Filters modal */}
      {showFilters && (
        <>
          <div className="mobile-backdrop" onClick={toggleFilters}></div>
          <div className="mobile-filters-menu">
            <div className="mobile-filters-header">
              <h3>{t('events.filters.filterEvents')}</h3>
              <button className="close-button" onClick={toggleFilters}>×</button>
            </div>
            <div className="mobile-filters-options">
              <button
                className={activeFilter === 'upcoming' ? 'active' : ''}
                onClick={() => handleFilterSelect('upcoming')}
              >
                {t('events.filters.upcomingEvents')}
              </button>
              <button
                className={activeFilter === 'past' ? 'active' : ''}
                onClick={() => handleFilterSelect('past')}
              >
                {t('events.filters.pastEvents')}
              </button>
              <button
                className={activeFilter === 'all' ? 'active' : ''}
                onClick={() => handleFilterSelect('all')}
              >
                {t('events.filters.allEvents')}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Floating action button */}
      <button
        className="mobile-action-button"
        onClick={onCreateEvent}
        aria-label={t('events.newEvent')}
      >
        <FontAwesomeIcon icon={faPlus} />
      </button>
    </div>
  );
}

export default MobileEventsList;